import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Types for Event Forms API
export interface EventForm {
  id: number;
  diagram: number | null;
  setUpDecorate: string | null;
  guestArrival: string | null;
  food: string | null;
  servers: string | null; // JSON string of employee IDs array
  name: string | null;
  eventFor: string | null;
  phone: string | null;
  email: string | null;
  depositDateManager: string | null;
  time: string | null;
  room: string | null;
  event: string | null;
  expGuest: number | null;
  finalCount: number | null;
  menu: string | null; // JSON string of menu options array
  chalkboardMessage: string | null;
  beverages: string | null;
  tables: string | null;
  linen: string | null;
  misc: string | null;
  tableSetUp: string | null;
  eventDate: string;
  status: string | null;
}

export interface CreateEventFormRequest {
  setUpDecorate?: string | null;
  guestArrival?: string | null;
  food?: string | null;
  servers?: string | null;
  name?: string | null;
  eventFor?: string | null;
  phone: string;
  email: string;
  depositDateManager?: string | null;
  time?: string | null;
  room?: string | null;
  event?: string | null;
  expGuest?: number | null;
  finalCount?: number | null;
  menu?: string | null;
  chalkboardMessage?: string | null;
  beverages?: string | null;
  tables?: string | null;
  linen?: string | null;
  misc?: string | null;
  tableSetUp?: string | null;
  eventDate: string;
  status?: string | null;
}

export interface UpdateEventFormRequest {
  setUpDecorate?: string | null;
  guestArrival?: string | null;
  food?: string | null;
  servers?: string | null;
  name?: string | null;
  eventFor?: string | null;
  phone?: string | null;
  email?: string | null;
  depositDateManager?: string | null;
  time?: string | null;
  room?: string | null;
  event?: string | null;
  expGuest?: number | null;
  finalCount?: number | null;
  menu?: string | null;
  chalkboardMessage?: string | null;
  beverages?: string | null;
  tables?: string | null;
  linen?: string | null;
  misc?: string | null;
  tableSetUp?: string | null;
  eventDate?: string;
  status?: string | null;
}

export interface PaginatedEventFormsResponse {
  data: EventForm[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  type?: 'history';
}

// Event Forms API calls
export const eventFormsApi = {
  // Get all event forms with pagination
  getEventForms: async (
    params: PaginationParams = {},
    token: string
  ): Promise<PaginatedEventFormsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.type) searchParams.append('type', params.type);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_FORMS.BASE)}?${searchParams.toString()}`;
    
    return apiRequest<PaginatedEventFormsResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get event form by ID
  getEventForm: async (id: number, token: string): Promise<EventForm> => {
    return apiRequest<EventForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_FORMS.BY_ID(id)),
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Create new event form
  createEventForm: async (data: CreateEventFormRequest, token: string): Promise<EventForm> => {
    return apiRequest<EventForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_FORMS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Update event form
  updateEventForm: async (
    id: number,
    data: UpdateEventFormRequest,
    token: string
  ): Promise<EventForm> => {
    return apiRequest<EventForm>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_FORMS.BY_ID(id)),
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  // Delete event form
  deleteEventForm: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_FORMS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },
};

